import type AgendaItem from "@/domain/entities/agenda/AgendaItem";
import type Email from "@/domain/entities/emails/Email";
import type { MailsListId } from "@/domain/entities/emails/Email";
import type MailDraft from "@/domain/entities/emails/MailDraft";
import type MailActionType from "@/domain/enums/MailActionType";

/**
 * What the assistant opens on its side, like Claude's panes: one group per kind (the mails, the
 * calendar), each with its tabs. Pure functions: the panel keeps the state, these say how it moves.
 */

export type PaneKind = "mail" | "event" | "compose";

export type MailTab = {
    kind: "mail";
    key: string;
    title: string;
    mail: Email;
    listId: MailsListId;
};

export type EventTab = {
    kind: "event";
    key: string;
    title: string;
    event: AgendaItem;
};

/**
 * A mail being written from an assistant's draft: a new mail, or a reply to `replyTo` (whose thread
 * opens in the mails group, above).
 */
export type ComposeTab = {
    kind: "compose";
    key: string;
    title: string;
    draft: MailDraft;
    replyTo?: { mail: Email; listId: MailsListId; actionType: MailActionType };
};

export type PaneTab = MailTab | EventTab | ComposeTab;

export type PaneGroup = { kind: PaneKind; tabs: PaneTab[]; activeKey: string };

/** The groups, in the order they were opened (the first one on top). */
export type SidePanes = PaneGroup[];

export const mailTabKey = (mail: Email) =>
    `mail:${mail.uniqueMailvistaId ?? mail.id}`;

export const eventTabKey = (event: AgendaItem) => `event:${event.id}`;

/**
 * Opens a tab, or brings it to the front if it is already open — refreshed with what was clicked
 * (a draft opened again replaces the previous one). Its group is created if needed.
 */
export const openTab = (panes: SidePanes, tab: PaneTab): SidePanes => {
    const group = panes.find((pane) => pane.kind === tab.kind);
    if (!group)
        return [...panes, { kind: tab.kind, tabs: [tab], activeKey: tab.key }];

    const known = group.tabs.some((open) => open.key === tab.key);
    const tabs = known
        ? group.tabs.map((open) => (open.key === tab.key ? tab : open))
        : [...group.tabs, tab];

    return panes.map((pane) =>
        pane === group ? { ...pane, tabs, activeKey: tab.key } : pane,
    );
};

export const activateTab = (
    panes: SidePanes,
    kind: PaneKind,
    key: string,
): SidePanes =>
    panes.map((pane) =>
        pane.kind === kind && pane.tabs.some((tab) => tab.key === key)
            ? { ...pane, activeKey: key }
            : pane,
    );

/**
 * Closes a tab; the one next to it comes to the front. The last tab of a group closes the group.
 */
export const closeTab = (
    panes: SidePanes,
    kind: PaneKind,
    key: string,
): SidePanes =>
    panes.flatMap((pane) => {
        if (pane.kind !== kind) return [pane];
        const index = pane.tabs.findIndex((tab) => tab.key === key);
        if (index < 0) return [pane];
        const tabs = pane.tabs.filter((tab) => tab.key !== key);
        if (tabs.length === 0) return [];
        const activeKey =
            pane.activeKey === key
                ? tabs[Math.min(index, tabs.length - 1)].key
                : pane.activeKey;

        return [{ ...pane, tabs, activeKey }];
    });

export const closeGroup = (panes: SidePanes, kind: PaneKind): SidePanes =>
    panes.filter((pane) => pane.kind !== kind);

/** Updates an open tab in place (an event answered from its pane). */
export const updateTab = (panes: SidePanes, tab: PaneTab): SidePanes =>
    panes.map((pane) =>
        pane.kind === tab.kind
            ? {
                  ...pane,
                  tabs: pane.tabs.map((open) =>
                      open.key === tab.key ? tab : open,
                  ),
              }
            : pane,
    );
